import { FilesetResolver, HandLandmarker } from "@mediapipe/tasks-vision"

// Local (same-origin) MediaPipe WASM — mirrors the face-landmarker. Loading the
// fileset from our own origin instead of a CDN avoids the third-party fetch
// stalling in locked-down in-app browsers (Instagram), and pins it to the
// installed package version instead of a floating `@latest`.
const WASM_PATH = "/mediapipe/wasm"

let landmarkerPromise: Promise<HandLandmarker> | null = null

/**
 * Lazily create the MediaPipe hand-landmarker once per session and cache it.
 * The heavy work — the WASM bundle + the ~7.5MB model — happens only on the first
 * call, so this is safe (and intended) to call early to warm it up. The instance
 * is never closed: it lives for the session so navigating between palm steps (or
 * quizzes) reuses the same one instead of reloading.
 *
 * Runs on the CPU delegate: this is a one-shot still-image detection, and the GPU
 * (WebGL2) delegate fails to initialize in some in-app webviews — Instagram's in
 * particular — which silently hangs the palm step. The face still-image path uses
 * CPU for the same reason.
 */
export function ensureHandLandmarker(): Promise<HandLandmarker> {
  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(WASM_PATH)
      return HandLandmarker.createFromOptions(vision, {
        baseOptions: { modelAssetPath: "/hand_landmarker.task", delegate: "CPU" },
        numHands: 1,
        runningMode: "IMAGE",
      })
    })().catch((error) => {
      // Reset so a later call can retry the load.
      landmarkerPromise = null
      console.error("[Palmistry] HandLandmarker init error:", error)
      throw error
    })
  }
  return landmarkerPromise
}
